// セマンティック検索（意味検索）画面
// 全書籍のメモを埋め込みベクトル化し、クエリと意味的に近い箇所を抽出
import {
  escapeHtml,
  screenHead,
  showScreen,
  showLoading,
  hideLoading,
  toast,
  renderLongText,
} from '../ui.js';
import { fetchEmbeddings } from '../api.js';
import {
  getAllBooks,
  getAllEmbeddings,
  getEmbedding,
  saveEmbedding,
} from '../cache.js';
import { showBookDetailScreen } from './book-detail.js';

const MAX_RESULTS = 10;

export async function showSemanticSearchScreen(opts = {}) {
  renderForm(opts);
}

function renderForm(opts = {}) {
  const initialQuery = opts.initialQuery || '';
  const html = `
    ${screenHead('意味で検索')}
    <p style="color:var(--dim);font-size:13px;line-height:1.75;margin:0 0 24px;">
      キーワードや短い問いを入力すると、全書籍のメモから「意味的に近い」箇所を AI が抽出します。<br/>
      例: 「光について」「死と時間」「権力と知の関係」
    </p>
    <div class="form-group">
      <label class="form-label">問い・キーワード</label>
      <textarea class="form-textarea" id="query" rows="2" placeholder="例: 不在の証言として写真を考える、または「光と存在」">${escapeHtml(initialQuery)}</textarea>
    </div>
    <button class="btn btn--ink btn--block" id="btn-go" type="button">
      <svg class="icon"><use href="#i-search"/></svg>
      <span>意味で検索</span>
    </button>
    <p style="margin-top:14px;font-size:11px;color:var(--mute);line-height:1.6;font-family:var(--font-mono);letter-spacing:0.08em;">
      使用モデル: text-embedding-3-small · 初回のみ全書籍を埋め込み（数秒）
    </p>
  `;
  const screen = showScreen('semantic-search', html);
  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
  screen.querySelector('#btn-go').addEventListener('click', async () => {
    const q = screen.querySelector('#query').value.trim();
    if (!q) {
      toast('問いを入力してください');
      return;
    }
    await runSearch(q);
  });
}

// メモ本文＋書名＋著者を埋め込みのソーステキストに使う。長すぎる場合は先頭 7000 字に切る。
function buildSource(book) {
  const note = (book.note || '').trim();
  if (!note) return '';
  return `${book.name}\n${book.author || ''}\n\n${note}`.slice(0, 7000);
}

// 軽量なハッシュ（前回と内容が同じならスキップする判定用）
function quickHash(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return String(h);
}

async function ensureEmbeddings(books) {
  // 既存埋め込みを取得
  const have = await getAllEmbeddings();
  const haveMap = new Map(have.map((e) => [e.bookId, e]));
  const need = [];
  for (const b of books) {
    const src = buildSource(b);
    if (!src) continue;
    const hash = quickHash(src);
    const cached = haveMap.get(b.id);
    if (!cached || cached.sourceHash !== hash) {
      need.push({ book: b, src, hash });
    }
  }
  if (need.length === 0) return;
  showLoading(`書籍を埋め込み中… (0/${need.length})`);
  // OpenAI Embeddings は 1 リクエストで複数テキスト可（上限 2048 件）
  const texts = need.map((x) => x.src);
  const { vectors } = await fetchEmbeddings(texts);
  for (let i = 0; i < need.length; i++) {
    await saveEmbedding(need[i].book.id, vectors[i], need[i].hash);
    const ov = document.getElementById('overlay-msg');
    if (ov) ov.textContent = `書籍を埋め込み中… (${i + 1}/${need.length})`;
  }
  hideLoading();
}

function cosine(a, b) {
  let dot = 0;
  let na = 0;
  let nb = 0;
  const len = Math.min(a.length, b.length);
  for (let i = 0; i < len; i++) {
    dot += a[i] * b[i];
    na += a[i] * a[i];
    nb += b[i] * b[i];
  }
  const denom = Math.sqrt(na) * Math.sqrt(nb);
  return denom ? dot / denom : 0;
}

async function runSearch(query) {
  const books = await getAllBooks();
  try {
    await ensureEmbeddings(books);
  } catch (e) {
    hideLoading();
    console.error(e);
    toast('埋め込み生成に失敗しました');
    return;
  }
  showLoading('検索中…');
  let queryVector;
  try {
    const { vectors } = await fetchEmbeddings([query]);
    queryVector = vectors[0];
  } catch (e) {
    hideLoading();
    toast('検索に失敗しました');
    return;
  }
  const embeds = await getAllEmbeddings();
  const scored = embeds
    .map((e) => {
      const book = books.find((b) => b.id === e.bookId);
      if (!book) return null;
      return { book, score: cosine(queryVector, e.vector) };
    })
    .filter(Boolean)
    .sort((a, b) => b.score - a.score)
    .slice(0, MAX_RESULTS);
  hideLoading();
  renderResult(query, scored);
}

// メモから最も関連する段落を抽出（簡易：クエリ語が含まれる段落を優先、なければ先頭）
function extractRelevantSnippet(note, query) {
  if (!note) return '';
  const paras = note.split(/\n{2,}/).filter((p) => p.trim().length > 20);
  if (paras.length === 0) return note.slice(0, 200);
  const terms = query.replace(/\s+/g, ' ').split(/\s+/).filter((s) => s.length >= 2);
  // クエリ語が最も多く含まれる段落
  let best = paras[0];
  let bestHits = 0;
  for (const p of paras) {
    const hits = terms.reduce((n, t) => n + (p.includes(t) ? 1 : 0), 0);
    if (hits > bestHits) {
      best = p;
      bestHits = hits;
    }
  }
  return best.length > 280 ? best.slice(0, 280) + '…' : best;
}

function renderResult(query, scored) {
  const html = `
    ${screenHead('意味で検索')}
    <div style="margin:0 0 20px;padding:12px 16px;border:1px solid var(--line);">
      <p style="margin:0 0 4px;font-family:var(--font-mono);font-size:10px;letter-spacing:0.22em;color:var(--mute);">query</p>
      <p style="margin:0;font-family:var(--font-serif);font-size:15px;line-height:1.6;">${escapeHtml(query)}</p>
    </div>
    <p style="color:var(--dim);font-size:13px;line-height:1.75;margin:0 0 16px;">
      ${scored.length} 件の関連書籍。スコア順。タップで詳細へ。
    </p>
    <ul style="list-style:none;padding:0;margin:0;">
      ${scored
        .map(({ book, score }) => {
          const snippet = extractRelevantSnippet(book.note || '', query);
          const scoreStr = (score * 100).toFixed(0);
          return `
        <li class="book-item" data-book-id="${escapeHtml(book.id)}" style="flex-direction:column;align-items:stretch;gap:6px;">
          <div style="display:flex;gap:12px;align-items:baseline;">
            <span style="font-family:var(--font-serif);font-size:15px;font-weight:500;flex:1;">${escapeHtml(book.name)}</span>
            <span style="font-family:var(--font-mono);font-size:11px;color:var(--mute);letter-spacing:0.08em;flex-shrink:0;">${scoreStr}%</span>
          </div>
          <p style="margin:0;font-size:12px;color:var(--mute);">${escapeHtml(book.author || '')}</p>
          ${snippet ? `<p style="margin:4px 0 0;font-family:var(--font-serif);font-size:13px;line-height:1.7;color:var(--dim);">${escapeHtml(snippet)}</p>` : ''}
        </li>
      `;
        })
        .join('')}
    </ul>
    <button class="btn btn--block btn--ghost" id="btn-back-form" type="button" style="margin-top:24px;">
      <svg class="icon"><use href="#i-search"/></svg>
      <span>別の問いで検索する</span>
    </button>
  `;
  const screen = showScreen('semantic-search-result', html);
  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
  screen.querySelector('#btn-back-form').addEventListener('click', () => renderForm());
  screen.querySelectorAll('[data-book-id]').forEach((li) => {
    li.addEventListener('click', () => showBookDetailScreen(li.dataset.bookId));
  });
}
